import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  Container,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import Loader from "../../../Components/Common/Loader";
import SearchableSelect from "../../../Components/Common/SearchableSelect";

import {
  adminGetAllAttendanceByPhoto,
  adminGetComboMember,
} from "helpers/backend_helpers";
import { formatDate } from "helpers/globalFunction";
import Select from "react-select";
import DetailModalPhotoAttendance from "./DetailModalPhotoAttendance";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import DownloadModalAttendance from "../DownloadModalAttendance";

const PhotoAttendance = () => {
  const [tableData, setTableData] = useState<any>([]);
  const [data, setData] = useState<any>({});
  const [sort, setSort] = useState("createTime");
  const [dir, setDir] = useState(-1);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);

  const [detailModal, setDetailModal] = useState<boolean>(false);
  const [downloadModal, setDownloadModal] = useState<boolean>(false);

  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [filterDate, setFilterDate] = useState<any>([]);
  const [filterStatus, setFilterStatus] = useState<any>(null);
  const [filterMember, setFilterMember] = useState<any>(null);
  const [filterCategory, setFilterCategory] = useState<any>(null);
  const [flatpickrKey, setFlatpickrKey] = useState(0);

  const getData = () => {
    setLoading(true);
    let query: any = {
      page: pagination.pageIndex,
      size: pagination.pageSize,
      sort: sort,
      dir: dir,
    };

    if (filterDate[0] && filterDate[1]) {
      query.startDate = moment(filterDate[0]).format("YYYY-MM-DD");
      query.endDate = moment(filterDate[1]).format("YYYY-MM-DD");
    }
    if (filterMember) {
      query.memberId = filterMember.value;
    }
    if (filterStatus) {
      query.status = filterStatus.value;
    }
    if (filterCategory) {
      query.memberCategory = filterCategory.value;
    }

    adminGetAllAttendanceByPhoto(query)
      .then((response: any) => {
        setTableData(
          response.content.map((el: any, index: any) => {
            const { checkInTime, checkOutTime, shift } = el;
            return {
              ...el,
              index: index + pagination.pageIndex * pagination.pageSize + 1,
              tanggal: formatDate(checkInTime, "dmy") || "-",
              namaShift: shift?.name || "-",
              jamMasuk: formatDate(checkInTime, "fullDateTime") || "-",
              jamKeluar: checkOutTime
                ? formatDate(checkOutTime, "fullDateTime")
                : "-",
            };
          })
        );
        setRowCount(response.totalElements);
        setTotalPages(response.totalPages);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const searchMembers = async (name: string) => {
    const query = { name };
    return await adminGetComboMember(query);
  };

  const formatMemberOption = (member: any) => {
    return {
      label: `${member.name} - ${member.noId}`,
      value: member._id,
    };
  };

  const handleResetFilter = () => {
    setFilterDate([]);
    setFilterStatus(null);
    setFilterMember(null);
    setFilterCategory(null);
    setFlatpickrKey((prev) => prev + 1);
  };

  const handleToggleFilter = () => {
    if (showFilter) {
      handleResetFilter();
    }
    setShowFilter(!showFilter);
  };

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "index",
        enableColumnFilter: false,
        enableSorting: false,
        size: 50,
      },
      {
        header: "Tanggal",
        accessorKey: "tanggal",
        enableColumnFilter: false,
        enableSorting: false,
        size: 150,
      },
      {
        header: "Nama Karyawan",
        accessorKey: "member.name",
        enableColumnFilter: false,
        enableSorting: false,
        size: 150,
      },
      {
        header: "No Reg",
        accessorKey: "member.noId",
        enableColumnFilter: false,
        enableSorting: false,
        size: 150,
      },
      {
        header: "Nama Shift",
        accessorKey: "namaShift",
        enableColumnFilter: false,
        enableSorting: false,
        size: 150,
      },
      {
        header: "Jam Masuk",
        accessorKey: "jamMasuk",
        enableColumnFilter: false,
        enableSorting: false,
        size: 200,
      },
      {
        header: "Jam Keluar",
        accessorKey: "jamKeluar",
        enableColumnFilter: false,
        enableSorting: false,
        size: 200,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        size: 150,
        cell: (cell: any) => {
          switch (cell.getValue()) {
            case "ON_TIME":
              return (
                <span className="badge text-uppercase bg-success-subtle text-success">
                  Tepat Waktu
                </span>
              );
            case "LATE":
              return (
                <span className="badge text-uppercase bg-danger-subtle text-danger">
                  Terlambat
                </span>
              );
            case "TOLERANCE":
              return (
                <span className="badge text-uppercase bg-warning-subtle text-warning">
                  Toleransi
                </span>
              );
            default:
              return (
                <span className="badge text-uppercase bg-info-subtle text-info">
                  -
                </span>
              );
          }
        },
      },
      {
        header: "Aksi",
        enableColumnFilter: false,
        enableSorting: false,
        size: 250,
        cell: (cellProps: any) => {
          return (
            <div className="hstack gap-2">
              <UncontrolledTooltip
                target={`detail-${cellProps.row.original._id}`}
              >
                Detail
              </UncontrolledTooltip>
              <button
                id={`detail-${cellProps.row.original._id}`}
                className="btn btn-sm btn-soft-info"
                onClick={() => {
                  setDetailModal(!detailModal);
                  setData(cellProps.row.original);
                }}
              >
                <i className="ri-eye-line" />
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  const status = [
    { value: "ON_TIME", label: "TEPAT WAKTU" },
    { value: "LATE", label: "TERLAMBAT" },
    { value: "TOLERANCE", label: "TOLERANSI" },
  ];

  const comboCategory = [
    { value: "EMPLOYEE", label: "EMPLOYEE" },
    { value: "SECURITY", label: "SECURITY" },
    { value: "CLEANING_SERVICE", label: "CLEANING SERVICE" },
    { value: "OFFICE_BOY", label: "OFFICE BOY" },
    { value: "DRIVER", label: "DRIVER" },
    { value: "GARDENER", label: "GARDENER" },
  ];

  useEffect(() => {
    getData();
  }, [
    pagination,
    sort,
    dir,
    filterDate,
    filterMember,
    filterStatus,
    filterCategory,
  ]);

  document.title = "Data Absen";

  return (
    <React.Fragment>
      <DetailModalPhotoAttendance
        show={detailModal}
        onCloseClick={() => setDetailModal(!detailModal)}
        data={data}
      />
      <DownloadModalAttendance
        show={downloadModal}
        onCloseClick={() => setDownloadModal(!downloadModal)}
      />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Data Absen" pageTitle="Absen" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="border-0">
                  <div className="d-flex align-items-center">
                    <h5 className="card-title mb-0 flex-grow-1">Data Absen</h5>
                    <div className="flex-shrink-0">
                      <div className="d-flex gap-2 flex-wrap">
                        <button
                          className="btn btn-soft-primary btn-sm"
                          onClick={handleToggleFilter}
                        >
                          <i className="ri-filter-3-line align-bottom me-1"></i>
                          Filters
                        </button>
                        <button
                          className="btn btn-soft-dark btn-sm"
                          onClick={() => setDownloadModal(!downloadModal)}
                        >
                          <i className="ri-file-download-line align-bottom me-1"></i>
                          Unduh Data Absen
                        </button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardBody className="pt-0">
                  <Collapse isOpen={showFilter}>
                    <Row className="g-3 mb-4">
                      <Col lg={3}>
                        <div>
                          <Flatpickr
                            key={flatpickrKey}
                            className="form-control"
                            options={{
                              mode: "range",
                              dateFormat: "d M Y",
                            }}
                            onChange={(e) => {
                              setFilterDate(e);
                            }}
                            value={filterDate}
                            placeholder="Pilih Tanggal"
                          />
                        </div>
                      </Col>
                      <Col lg={3}>
                        <SearchableSelect
                          value={filterMember}
                          onChange={setFilterMember}
                          onSearch={searchMembers}
                          formatOptionLabel={formatMemberOption}
                          placeholder="Cari Karyawan"
                          minSearchLength={2}
                          itemsPerPage={5}
                          isClearable
                        />
                      </Col>
                      <Col lg={3}>
                        <Select
                          value={filterCategory}
                          onChange={(e: any) => {
                            setFilterCategory(e);
                          }}
                          options={comboCategory}
                          className="basic-multi-select"
                          classNamePrefix="select"
                          placeholder="Pilih Kategori Karyawan"
                          isClearable
                        />
                      </Col>
                      <Col lg={3}>
                        <Select
                          value={filterStatus}
                          onChange={(e: any) => {
                            setFilterStatus(e);
                          }}
                          options={status}
                          className="basic-multi-select"
                          classNamePrefix="select"
                          placeholder="Pilih Status"
                          isClearable
                        />
                      </Col>
                      <Col lg={12}>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-soft-secondary btn-sm"
                            onClick={handleResetFilter}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>
                            Reset
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </Collapse>
                  <div>
                    {loading ? (
                      <Loader />
                    ) : (
                      <TableContainer
                        columns={columns}
                        data={tableData || []}
                        customPageSize={10}
                        theadClass="text-muted text-uppercase"
                        manualPagination
                        pagination={pagination}
                        pageCount={totalPages}
                        onPaginationChange={setPagination}
                        divClass="table-responsive"
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PhotoAttendance;
